import React, { Component } from 'react';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import includes from 'lodash/includes';
import isString from 'lodash/isString';
import Data from '../../data';
import Grid from '../../grid';
import Modal from '../../modal';
import UI from '../../ui';
import Utils from '../../utils';
import Util from '../Util';
import { InternalCapability, Capability } from '../../data/account';
import { Carrier } from '../../data/carriers';
import CarrierModule from '../../carrier';

const { Util: CarrierUtil } = CarrierModule;
const { CalculatedData, ServiceDataHelper, ServiceType, PackageType, Service } = Data;
const { GridPanel } = Grid;
const { ModalContext } = Modal;
const { WrappedButton, WrappedCheckbox, WrappedInputText } = UI;
const { Session } = Utils;

const autoGroupColumnDef = {
	field: 'packageName',
	cellRenderer: 'agGroupCellRenderer',
	flex: 1,
	suppressSizeToFit: false,
	cellRendererParams: {
		checkbox: true,
		suppressCount: true,
	},
	cellClass: ({ data, node }) => {
		if (data) {
			const { serviceType, packageType } = node.data;
			const serviceTypesLookup = [
				ServiceType.UspsPriorityMailExpress,
				ServiceType.UspsPriorityMailExpressIntl,
			];
			const packageTypesLookup = [
				PackageType.UspsFlatRateEnvelope,
				PackageType.UspsLegalFlatRateEnvelope,
				PackageType.UspsPaddedFlatRateEnvelope,
			];
			const priorityMailExpressAppendix =
				includes(serviceTypesLookup, serviceType) &&
				includes(packageTypesLookup, packageType)
					? '-pme'
					: '';

			const packageCls = isString(packageType)
				? packageType.toLowerCase().replace(/_/g, '-') + priorityMailExpressAppendix
				: null;

			return `${packageCls}-logo service-options-row`;
		} else {
			return 'service-group-row';
		}
	},
};

const columnDefs = [
	{
		field: 'serviceName',
		filter: true,
		rowGroup: true,
		hide: true,

		// this is needed to avoid toString=[object,object] result with objects
		getQuickFilterText: ({ data }) => `${data.value}${data.serviceName}${data.packageName}`,
	},
];

export default class ServiceOptionsView extends Component {
	constructor(props) {
		super(props);

		const { ShowUnavailableServices = '0' } = Session.getPreferences();
		const rowData = this.getPreferredServices();

		this.state = {
			rowData,
			searchText: '',
			showUnavailableServices: ShowUnavailableServices,
		};

		this.gridApi = null;
	}

	getPreferredServices = () => {
		const { PreferredServices = '' } = Session.getPreferences();
		const preferredServices =
			PreferredServices === ''
				? {}
				: JSON.parse(PreferredServices).reduce((result, service) => {
						result[service.ValueStr] = service.checked;
						return result;
				  }, {});

		// translate SWSIM preferred data to SERA, but keep it as is if it's already in SERA format
		const seraPreferredServices =
			typeof preferredServices.USFC_Postcard !== 'undefined'
				? Util.translatePreferredServicesToSERA(preferredServices)
				: preferredServices;

		// store map locally to translate service options data back to SWSIM later
		Util.buildpreferredServicesMap();

		/**
		 * Transfer the 'checked' value from the PreferredServices array to the Services array.
		 * If PreferredServices is empty, every service's checked flag is true by default.
		 */
		return CalculatedData.allServices
			.map(service => {
				service.checked =
					isEmpty(seraPreferredServices) || seraPreferredServices[service.value] === true;
				service.serviceName = service.serviceName.replace(/<sup>&reg;<\/sup>/gi, '®');

				return service;
			})
			.filter(service => {
				const { serviceName, value } = service;
				const swsimServiceType = ServiceDataHelper.translateServiceTypeToSWSIM(
					service.serviceType
				);
				const carrier = ServiceDataHelper.getCarrierValue(swsimServiceType);
				const hideUps =
					carrier === Carrier.UPS &&
					!Session.hasCapability(InternalCapability.DisplayUpsByDefault) &&
					!CarrierUtil.isCarrierConfigured(Carrier.UPS);
				const hideDhl =
					carrier === Carrier.DHLExpress &&
					!Session.hasCapability(InternalCapability.DisplayDhlByDefault) &&
					!CarrierUtil.isCarrierConfigured(Carrier.DHLExpress);
				const hideFedex =
					carrier === Carrier.FedEx &&
					!Session.hasCapability(InternalCapability.DisplayFedexByDefault) &&
					!CarrierUtil.isCarrierConfigured(Carrier.FedEx);
				const hideGp =
					carrier === Carrier.GlobalPost &&
					!serviceName.includes('SmartSaver') &&
					!Session.hasCapability(Capability.Globalpost);
				const hideGpSmartSaver =
					carrier === Carrier.GlobalPost &&
					serviceName.includes('SmartSaver') &&
					!Session.hasCapability(Capability.Globalpost) &&
					!Session.hasCapability(Capability.GpSmartSaver);
				const hideFirstClassPackage =
					value === Service.UspsFirstClassMailPackage &&
					!Session.hasCapability(Capability.FcGap);
				const hideParcelSelect =
					value === Service.UspsParcelSelectPackage &&
					!Session.hasCapability(Capability.PsGap);

				if (
					hideUps ||
					hideDhl ||
					hideFedex ||
					hideGp ||
					hideGpSmartSaver ||
					hideFirstClassPackage ||
					hideParcelSelect
				) {
					return false;
				}

				return true;
			});
	};

	filterBySearch = () => {
		const { searchText } = this.state;

		this.gridApi.setQuickFilter(searchText);
	};

	onChangeSearch = ({ target: { value } }) => {
		this.setState({
			searchText: value,
		});
	};

	onKeyPressEnter = e => {
		const { key } = e;

		if (key === 'Enter') {
			e.preventDefault();

			this.filterBySearch();
		}
	};

	onChangeCheckbox = () => {
		const { showUnavailableServices } = this.state;
		const { showError } = this.context;

		/**
		 * For backward compatibility, the checkbox is also checked if the value is 'true'.
		 * Moving forward, we should only store '1' or '0' for checked or unchecked.
		 */
		const newPreferences = {
			showUnavailableServices:
				showUnavailableServices === '0' || showUnavailableServices === false ? '1' : '0',
		};

		this.setState(newPreferences);

		Util.saveNewPreferences(newPreferences, showError);
	};

	onClickDone = () => {
		const { completionCallback } = this.props;

		if (isFunction(completionCallback)) {
			completionCallback({ close: true });
		}
	};

	onGridReady = ({ api }) => {
		const { rowData } = this.state;
		const clearSelection = false;
		const serviceCount = rowData.length;
		this.gridApi = api;

		// check if the node should be checked
		this.gridApi.forEachLeafNode((node, i) => {
			const { data } = node;
			const checked = data.checked === true;

			// only run callbacks on the last iteration to prevent unnecessary renders
			if (serviceCount === i + 1) {
				node.setSelected(checked, clearSelection, false);
			} else {
				node.setSelected(checked, clearSelection, true);
			}
		});
	};

	// change the 'checked' property when selecting/deselecting an item in the grid
	onSelectionChanged = selection => {
		const { rowData } = this.state;
		const { showError } = this.context;

		const selectionLookup = selection.reduce((result, { data = {}, selected }) => {
			result[data.value] = selected;
			return result;
		}, {});

		/**
		 * TODO: remove conversion to SWSIM once Mail/Orders uses SERA data
		 */
		const preferredServices = rowData.flatMap(service => {
			const serviceValue = service.value;
			const newValue = Util.translatePreferredServicesToSWSIM(serviceValue);
			const newService = [
				{
					ValueStr: newValue,
					checked: selectionLookup[service.value] === true,
				},
			];

			// exclude undefined values
			if (!newValue) {
				return [];
			}

			// apply check to SWSIM Large/Oversized Packages in Mail/Orders if SERA Package is selected
			if (newValue === 'USPM_Package') {
				newService.push({
					ValueStr: 'USPM_LargePackage',
					checked: selectionLookup[service.value] === true,
				});
			} else if (newValue === 'USXM_Package') {
				newService.push({
					ValueStr: 'USXM_LargePackage',
					checked: selectionLookup[service.value] === true,
				});
			} else if (newValue === 'USPS_Package') {
				newService.push(
					{
						ValueStr: 'USPS_LargePackage',
						checked: selectionLookup[service.value] === true,
					},
					{
						ValueStr: 'USPS_OversizedPackage',
						checked: selectionLookup[service.value] === true,
					}
				);
			} else if (newValue === 'USGA_Package') {
				newService.push({
					ValueStr: 'USGA_LargePackage',
					checked: selectionLookup[service.value] === true,
				});
			}

			return newService;
		});

		this.setState({
			rowData: rowData.map(service => ({
				...service,
				checked: selectionLookup[service.value] === true,
			})),
		});

		Util.saveNewPreferences(
			{ preferredServices: JSON.stringify(preferredServices) },
			showError
		);
	};

	getRowNodeId = data => data.value;

	render() {
		const { rowData, searchText, showUnavailableServices } = this.state;

		const isShowUnavailableChecked =
			showUnavailableServices === '1' || showUnavailableServices === true;

		return (
			<div className="settings-common-serviceoptions-view">
				<div className="settings-body">
					<div className="settings-content-header">
						<p>Service Options</p>
						<hr />
					</div>
					<div className="p-grid p-dir-col">
						<div className="p-col-12">
							<p>Select which options would be shown in the Service list.</p>
						</div>
						<div className="p-col-12 settings-search">
							<WrappedInputText
								name="searchBar"
								placeholder="Search Services"
								onBlur={this.filterBySearch}
								onChange={this.onChangeSearch}
								onKeyPress={this.onKeyPressEnter}
								value={searchText}
								dataqa="searchbar"
							/>
							<span
								className="pi pi-search search-icon"
								onClick={this.filterBySearch}
								tabIndex={0}
								role="button"
								aria-label="Search"
							/>
						</div>
						<div className="p-col-12 settings-list">
							<GridPanel
								rowData={rowData}
								columnDefs={columnDefs}
								showPagination={false}
								onGridReady={this.onGridReady}
								onSelectionChanged={this.onSelectionChanged}
								headerHeight={0}
								autoGroupColumnDef={autoGroupColumnDef}
								groupSelectsChildren
								groupSelectsFiltered
								rowMultiSelectWithClick
								deltaRowDataMode
								getRowNodeId={this.getRowNodeId}
								dataqa="serviceoptionsview-grid"
							/>
						</div>
						<div className="p-col-12">
							<p>
								When this option is checked, services that are unavailable for your
								current mailpiece will be shown as disabled. When unchecked, they
								will instead be hidden.
							</p>
						</div>
						<div className="p-col-12">
							<WrappedCheckbox
								inputId="settings-service-show-unavailable-services"
								htmlFor="settings-service-show-unavailable-services"
								name="showUnavailableServices"
								value={showUnavailableServices}
								onChange={this.onChangeCheckbox}
								checked={isShowUnavailableChecked}
								dataqa="showUnavailableServices-checkbox"
							>
								Show unavailable services
							</WrappedCheckbox>
						</div>
					</div>
				</div>
				<div className="settings-content-footer">
					<WrappedButton
						className="settings-button"
						label="Done"
						type="button"
						onClick={this.onClickDone}
						dataqa="serviceoptionsview-done-button"
					/>
				</div>
			</div>
		);
	}
}

ServiceOptionsView.contextType = ModalContext;
